import React, { useState, useEffect, useRef } from 'react';
import { fetchCustomerEmployeeSalary, saveCustomerEmployeeSalary, updateCustomerEmployeeSalary, fetchAllEmployeesForSalaryConfig, fetchEmployeesByCustomer, fetchCustomers, fetchEmployeesWithSalaryConfig, fetchOccupationCategories, fetchInsureCategories, bulkSaveCustomerEmployeeSalary } from '../lib/api';
import { formatSalary, parseSalary } from '../../../shared/utils/helpers';

interface SalaryConfigurationData {
  Oid?: string;
  CustomerEmployeeId: string;
  Year: number;
  S1: number | null;
  S2: number | null;
  S3: number | null;
  S4: number | null;
  S5: number | null;
  S6: number | null;
  S7: number | null;
  S8: number | null;
  S9: number | null;
  S10: number | null;
  S11: number | null;
  S12: number | null;
  S1_OccupationCategoryId: string | null;
  S2_OccupationCategoryId: string | null;
  S3_OccupationCategoryId: string | null;
  S4_OccupationCategoryId: string | null;
  S5_OccupationCategoryId: string | null;
  S6_OccupationCategoryId: string | null;
  S7_OccupationCategoryId: string | null;
  S8_OccupationCategoryId: string | null;
  S9_OccupationCategoryId: string | null;
  S10_OccupationCategoryId: string | null;
  S11_OccupationCategoryId: string | null;
  S12_OccupationCategoryId: string | null;
  S1_InsureCategoryId: string | null;
  S2_InsureCategoryId: string | null;
  S3_InsureCategoryId: string | null;
  S4_InsureCategoryId: string | null;
  S5_InsureCategoryId: string | null;
  S6_InsureCategoryId: string | null;
  S7_InsureCategoryId: string | null;
  S8_InsureCategoryId: string | null;
  S9_InsureCategoryId: string | null;
  S10_InsureCategoryId: string | null;
  S11_InsureCategoryId: string | null;
  S12_InsureCategoryId: string | null;
}

interface Employee {
  // CustomerEmployee table fields
  Oid: string;
  Code: string;
  Name: string;
  LastName: string;
  TIN: string;
  InsureNumber: string;
  InsureCategoryId: string | null;
  Salary: number | null;
  OccupationCategoryId: string | null;
  CustomerOid: string;
  
  // Computed fields
  EmployeeName: string; // Name + LastName
  CustomerId?: string;
  CustomerName?: string;
  
  // CustomerEmployeeSalary table fields (if exists)
  SalaryConfigOid?: string;
  SalaryConfigYear?: number;
  HasSalaryConfig?: boolean;
  S1?: number | null;
  S2?: number | null;
  S3?: number | null;
  S4?: number | null;
  S5?: number | null;
  S6?: number | null;
  S7?: number | null;
  S8?: number | null;
  S9?: number | null;
  S10?: number | null;
  S11?: number | null;
  S12?: number | null;
  S1_OccupationCategoryId?: string | null;
  S2_OccupationCategoryId?: string | null;
  S3_OccupationCategoryId?: string | null;
  S4_OccupationCategoryId?: string | null;
  S5_OccupationCategoryId?: string | null;
  S6_OccupationCategoryId?: string | null;
  S7_OccupationCategoryId?: string | null;
  S8_OccupationCategoryId?: string | null;
  S9_OccupationCategoryId?: string | null;
  S10_OccupationCategoryId?: string | null;
  S11_OccupationCategoryId?: string | null;
  S12_OccupationCategoryId?: string | null;
  S1_InsureCategoryId?: string | null;
  S2_InsureCategoryId?: string | null;
  S3_InsureCategoryId?: string | null;
  S4_InsureCategoryId?: string | null;
  S5_InsureCategoryId?: string | null;
  S6_InsureCategoryId?: string | null;
  S7_InsureCategoryId?: string | null;
  S8_InsureCategoryId?: string | null;
  S9_InsureCategoryId?: string | null;
  S10_InsureCategoryId?: string | null;
  S11_InsureCategoryId?: string | null;
  S12_InsureCategoryId?: string | null;
}

interface Category {
  Oid: string;
  Code: string;
  Name: string;
}

interface SalaryConfigurationPopupProps {
  isVisible: boolean;
  onClose: () => void;
  initialYear?: number;
  onSaveSuccess?: () => void;
}

const SalaryConfigurationPopup: React.FC<SalaryConfigurationPopupProps> = ({
  isVisible,
  onClose,
  initialYear,
  onSaveSuccess
}) => {
  const [salaryData, setSalaryData] = useState<SalaryConfigurationData>({
    CustomerEmployeeId: '',
    Year: initialYear || new Date().getFullYear(),
    S1: null,
    S2: null,
    S3: null,
    S4: null,
    S5: null,
    S6: null,
    S7: null,
    S8: null,
    S9: null,
    S10: null,
    S11: null,
    S12: null,
    S1_OccupationCategoryId: null,
    S2_OccupationCategoryId: null,
    S3_OccupationCategoryId: null,
    S4_OccupationCategoryId: null,
    S5_OccupationCategoryId: null,
    S6_OccupationCategoryId: null,
    S7_OccupationCategoryId: null,
    S8_OccupationCategoryId: null,
    S9_OccupationCategoryId: null,
    S10_OccupationCategoryId: null,
    S11_OccupationCategoryId: null,
    S12_OccupationCategoryId: null,
    S1_InsureCategoryId: null,
    S2_InsureCategoryId: null,
    S3_InsureCategoryId: null,
    S4_InsureCategoryId: null,
    S5_InsureCategoryId: null,
    S6_InsureCategoryId: null,
    S7_InsureCategoryId: null,
    S8_InsureCategoryId: null,
    S9_InsureCategoryId: null,
    S10_InsureCategoryId: null,
    S11_InsureCategoryId: null,
    S12_InsureCategoryId: null,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(initialYear || new Date().getFullYear());
  const [selectedCustomer, setSelectedCustomer] = useState<{Oid: string, CustomerName: string, CustomerID: string} | null>(null);
  const [customers, setCustomers] = useState<{Oid: string, CustomerName: string, CustomerID: string}[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [occupationCategories, setOccupationCategories] = useState<Category[]>([]);
  const [insureCategories, setInsureCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<{[key: string]: string}>({});
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<{[key: string]: number}>({});
  const occupationDropdownRef = useRef<HTMLDivElement>(null);
  const insureDropdownRef = useRef<HTMLDivElement>(null);

  // Load customers and categories when popup opens
  useEffect(() => {
    if (isVisible) {
      loadCustomers();
      loadCategories();
    }
  }, [isVisible]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      if (activeDropdown && activeDropdown.includes('OccupationCategory') && 
          occupationDropdownRef.current && 
          !occupationDropdownRef.current.contains(target)) {
        setActiveDropdown(null);
        setSearchTerm(prev => ({...prev, [activeDropdown]: ''}));
      } else if (activeDropdown && activeDropdown.includes('InsureCategory') && 
                 insureDropdownRef.current && 
                 !insureDropdownRef.current.contains(target)) {
        setActiveDropdown(null);
        setSearchTerm(prev => ({...prev, [activeDropdown]: ''}));
      }
    };

    if (activeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);

  // Load employees when customer is selected
  useEffect(() => {
    if (selectedCustomer) {
      loadEmployeesByCustomer();
    } else {
      setEmployees([]);
      setSelectedEmployee(null);
    }
  }, [selectedCustomer]);

  // Load salary data when employee is selected
  useEffect(() => {
    if (selectedEmployee && selectedYear) {
      loadSalaryData();
    }
  }, [selectedEmployee, selectedYear]);

  const loadCustomers = async () => {
    try {
      setCustomersLoading(true);
      setError(null);
      
      const customersData = await fetchCustomers();
      setCustomers(customersData);
    } catch (err) {
      console.error('Error loading customers:', err);
      setError('Харилцагчийн жагсаалт ачааллахад алдаа гарлаа');
    } finally {
      setCustomersLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      setCategoriesLoading(true);
      setError(null);
      
      const [occupationData, insureData] = await Promise.all([
        fetchOccupationCategories(),
        fetchInsureCategories()
      ]);
      
      setOccupationCategories(occupationData);
      setInsureCategories(insureData);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Ангиллын жагсаалт ачааллахад алдаа гарлаа');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const loadEmployeesByCustomer = async () => {
    if (!selectedCustomer) return;
    
    try {
      setEmployeesLoading(true);
      setError(null);
      
      // Use the new API that joins CustomerEmployee + CustomerEmployeeSalary
      const employeesData = await fetchEmployeesWithSalaryConfig(selectedCustomer.Oid, selectedYear);
      setEmployees(employeesData);
    } catch (err) {
      console.error('Error loading employees by customer:', err);
      setError('Ажилтны жагсаалт ачааллахад алдаа гарлаа');
    } finally {
      setEmployeesLoading(false);
    }
  };

  const loadSalaryData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!selectedEmployee || !selectedYear) return;
      
      const data = await fetchCustomerEmployeeSalary(selectedEmployee.Oid, selectedYear);
      
      if (data) {
        setSalaryData({
          ...data,
          CustomerEmployeeId: selectedEmployee.Oid,
          Year: selectedYear
        });
      } else {
        // Reset to default values if no data found
        setSalaryData({
          CustomerEmployeeId: selectedEmployee.Oid,
          Year: selectedYear,
          S1: null,
          S2: null,
          S3: null,
          S4: null,
          S5: null,
          S6: null,
          S7: null,
          S8: null,
          S9: null,
          S10: null,
          S11: null,
          S12: null,
          S1_OccupationCategoryId: null,
          S2_OccupationCategoryId: null,
          S3_OccupationCategoryId: null,
          S4_OccupationCategoryId: null,
          S5_OccupationCategoryId: null,
          S6_OccupationCategoryId: null,
          S7_OccupationCategoryId: null,
          S8_OccupationCategoryId: null,
          S9_OccupationCategoryId: null,
          S10_OccupationCategoryId: null,
          S11_OccupationCategoryId: null,
          S12_OccupationCategoryId: null,
          S1_InsureCategoryId: null,
          S2_InsureCategoryId: null,
          S3_InsureCategoryId: null,
          S4_InsureCategoryId: null,
          S5_InsureCategoryId: null,
          S6_InsureCategoryId: null,
          S7_InsureCategoryId: null,
          S8_InsureCategoryId: null,
          S9_InsureCategoryId: null,
          S10_InsureCategoryId: null,
          S11_InsureCategoryId: null,
          S12_InsureCategoryId: null,
        });
      }
    } catch (err) {
      console.error('Error loading salary data:', err);
      setError('Цалингийн мэдээлэл ачааллахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof SalaryConfigurationData, value: string | number | null) => {
    console.log('🔄 handleInputChange called:', { field, value });
    setSalaryData(prev => ({
      ...prev,
      [field]: value === '' ? null : value
    }));
  };

  const handleSearchChange = (field: string, value: string) => {
    setSearchTerm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getFilteredCategories = (categories: Category[], searchField: string) => {
    const search = searchTerm[searchField] || '';
    if (!search) return categories;
    return categories.filter(cat => 
      cat.Code.toLowerCase().includes(search.toLowerCase()) ||
      cat.Name.toLowerCase().includes(search.toLowerCase())
    );
  };

  const getSelectedCategoryText = (categories: Category[], selectedId: string) => {
    const category = categories.find(cat => cat.Oid === selectedId);
    return category ? `${category.Code} - ${category.Name}` : '';
  };

  const handleKeyboardNavigation = (fieldName: string, e: React.KeyboardEvent, categories: Category[]) => {
    if (!activeDropdown || activeDropdown !== fieldName) return;
    
    const filteredCategories = getFilteredCategories(categories, fieldName);
    const currentIndex = hoveredIndex[fieldName] || -1;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = Math.min(currentIndex + 1, filteredCategories.length - 1);
        setHoveredIndex(prev => ({...prev, [fieldName]: nextIndex}));
        break;
      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = Math.max(currentIndex - 1, -1);
        setHoveredIndex(prev => ({...prev, [fieldName]: prevIndex}));
        break;
      case 'Enter':
        e.preventDefault();
        if (currentIndex >= 0 && currentIndex < filteredCategories.length) {
          const selectedCategory = filteredCategories[currentIndex];
          handleInputChange(fieldName as keyof SalaryConfigurationData, selectedCategory.Oid);
          setSearchTerm(prev => ({...prev, [fieldName]: ''}));
          setActiveDropdown(null);
          setHoveredIndex(prev => ({...prev, [fieldName]: -1}));
        }
        break;
      case 'Escape':
        e.preventDefault();
        setActiveDropdown(null);
        setSearchTerm(prev => ({...prev, [fieldName]: ''}));
        setHoveredIndex(prev => ({...prev, [fieldName]: -1}));
        break;
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      let result;
      if (salaryData.Oid) {
        // Update existing record
        result = await updateCustomerEmployeeSalary(salaryData.Oid, salaryData);
      } else {
        // Create new record
        result = await saveCustomerEmployeeSalary(salaryData);
      }

      if (result.success) {
        console.log('✅ Salary configuration saved successfully');
        // onSaveSuccess?.(); // Commented out to prevent automatic refresh
        onClose();
      } else {
        setError(result.message || 'Цалингийн тохиргоо хадгалахад алдаа гарлаа');
      }
    } catch (err) {
      console.error('Error saving salary configuration:', err);
      setError('Цалингийн тохиргоо хадгалахад алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!selectedCustomer || employees.length === 0) {
        setError('Харилцагч болон ажилчдын мэдээлэл шаардлагатай');
        return;
      }

      // Prepare employee data for bulk save
      const employeesData = employees.map(employee => ({
        CustomerEmployeeId: employee.Oid,
        S1: employee.S1 || null,
        S2: employee.S2 || null,
        S3: employee.S3 || null,
        S4: employee.S4 || null,
        S5: employee.S5 || null,
        S6: employee.S6 || null,
        S7: employee.S7 || null,
        S8: employee.S8 || null,
        S9: employee.S9 || null,
        S10: employee.S10 || null,
        S11: employee.S11 || null,
        S12: employee.S12 || null,
        S1_OccupationCategoryId: employee.S1_OccupationCategoryId || null,
        S2_OccupationCategoryId: employee.S2_OccupationCategoryId || null,
        S3_OccupationCategoryId: employee.S3_OccupationCategoryId || null,
        S4_OccupationCategoryId: employee.S4_OccupationCategoryId || null,
        S5_OccupationCategoryId: employee.S5_OccupationCategoryId || null,
        S6_OccupationCategoryId: employee.S6_OccupationCategoryId || null,
        S7_OccupationCategoryId: employee.S7_OccupationCategoryId || null,
        S8_OccupationCategoryId: employee.S8_OccupationCategoryId || null,
        S9_OccupationCategoryId: employee.S9_OccupationCategoryId || null,
        S10_OccupationCategoryId: employee.S10_OccupationCategoryId || null,
        S11_OccupationCategoryId: employee.S11_OccupationCategoryId || null,
        S12_OccupationCategoryId: employee.S12_OccupationCategoryId || null,
        S1_InsureCategoryId: employee.S1_InsureCategoryId || null,
        S2_InsureCategoryId: employee.S2_InsureCategoryId || null,
        S3_InsureCategoryId: employee.S3_InsureCategoryId || null,
        S4_InsureCategoryId: employee.S4_InsureCategoryId || null,
        S5_InsureCategoryId: employee.S5_InsureCategoryId || null,
        S6_InsureCategoryId: employee.S6_InsureCategoryId || null,
        S7_InsureCategoryId: employee.S7_InsureCategoryId || null,
        S8_InsureCategoryId: employee.S8_InsureCategoryId || null,
        S9_InsureCategoryId: employee.S9_InsureCategoryId || null,
        S10_InsureCategoryId: employee.S10_InsureCategoryId || null,
        S11_InsureCategoryId: employee.S11_InsureCategoryId || null,
        S12_InsureCategoryId: employee.S12_InsureCategoryId || null,
      }));

      const result = await bulkSaveCustomerEmployeeSalary(selectedCustomer.Oid, selectedYear, employeesData);

      if (result.success) {
        console.log('✅ Bulk salary configuration saved successfully');
        onSaveSuccess?.();
        onClose();
      } else {
        setError(result.message || 'Бүх ажилчдын цалингийн тохиргоо хадгалахад алдаа гарлаа');
      }
    } catch (err) {
      console.error('Error bulk saving salary configuration:', err);
      setError('Бүх ажилчдын цалингийн тохиргоо хадгалахад алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };


  if (!isVisible) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg shadow-xl w-[90vw] min-h-[600px] max-h-[90vh] mx-4 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-blue-600 text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">Цалингийн тохиргоо</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Ачааллаж байна...</span>
            </div>
          ) : (
            <>
              {/* Step by Step Selection */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">     
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Step 1: Year Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      1. Жил сонгох
                    </label>
                    <select
                      value={selectedYear}
                      onChange={(e) => {
                        setSelectedYear(parseInt(e.target.value));
                        setSelectedEmployee(null); // Reset employee when year changes
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[200px]"
                    >
                      {Array.from({ length: 10 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Step 2: Customer Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      2. Харилцагч сонгох
                    </label>
                    {customersLoading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        <span className="text-gray-600 text-sm">Ачааллаж байна...</span>
                      </div>
                    ) : (
                      <select
                        value={selectedCustomer?.Oid || ''}
                        onChange={(e) => {
                          const customer = customers.find(cust => cust.Oid === e.target.value);
                          setSelectedCustomer(customer || null);
                          setSelectedEmployee(null); // Reset employee when customer changes
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[200px]"
                      >
                        <option value="">Харилцагч сонгоно уу...</option>
                        {customers.map((customer, index) => (
                          <option key={`${customer.Oid}-${index}`} value={customer.Oid}>
                            {customer.CustomerName} ({customer.CustomerID})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                </div>

              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                </div>
              )}

              {/* All Employees Salary Configuration - Show when customer is selected */}
              {selectedCustomer && employees.length > 0 && (
                <div className="space-y-6">                
                  
                  {/* All Employees Table */}
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Ажилтны нэр
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Регистр
                            </th>
                            {Array.from({ length: 12 }, (_, i) => (
                              <th key={i + 1} className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {i + 1}-р сар
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {employees.map((employee, empIndex) => (
                            <tr key={employee.Oid} className={empIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {employee.Name} {employee.LastName}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {employee.Code}
                              </td>
                              {Array.from({ length: 12 }, (_, monthIndex) => {
                                const monthNum = monthIndex + 1;
                                const sField = `S${monthNum}` as keyof SalaryConfigurationData;
                                const occupationField = `S${monthNum}_OccupationCategoryId` as keyof SalaryConfigurationData;
                                const insureField = `S${monthNum}_InsureCategoryId` as keyof SalaryConfigurationData;
                                
                                return (
                                  <td key={monthNum} className="px-2 py-2">
                                    <div className="space-y-1">
                                      {/* Salary Input */}
                                      <input
                                        type="text"
                                        value={formatSalary(employee[sField] || 0)}
                                        onChange={(e) => {
                                          const numericValue = parseSalary(e.target.value);
                                          // Update employee data
                                          setEmployees(prev => prev.map(emp => 
                                            emp.Oid === employee.Oid 
                                              ? { ...emp, [sField]: numericValue }
                                              : emp
                                          ));
                                        }}
                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        placeholder="0"
                                      />
                                      
                                      {/* Occupation Category */}
                                      <div className="relative">
                                        <input
                                          type="text"
                                          value={getSelectedCategoryText(occupationCategories, String(employee[occupationField] || ''))}
                                          onChange={(e) => handleSearchChange(`${employee.Oid}_${occupationField}`, e.target.value)}
                                          onFocus={() => {
                                            setSearchTerm(prev => ({...prev, [`${employee.Oid}_${occupationField}`]: ''}));
                                            setActiveDropdown(`${employee.Oid}_${occupationField}`);
                                          }}
                                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                          placeholder="Мэргэжил"
                                        />
                                        {activeDropdown === `${employee.Oid}_${occupationField}` && (
                                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg overflow-y-auto max-h-32">
                                            {getFilteredCategories(occupationCategories, `${employee.Oid}_${occupationField}`).map((category, index) => (
                                              <div
                                                key={category.Oid}
                                                onClick={() => {
                                                  setEmployees(prev => prev.map(emp => 
                                                    emp.Oid === employee.Oid 
                                                      ? { ...emp, [occupationField]: category.Oid }
                                                      : emp
                                                  ));
                                                  setActiveDropdown(null);
                                                  setSearchTerm(prev => ({...prev, [`${employee.Oid}_${occupationField}`]: ''}));
                                                }}
                                                className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                                              >
                                                <div className="font-medium">{category.Code}</div>
                                                <div className="text-gray-600 truncate">{category.Name}</div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Insurance Category */}
                                      <div className="relative">
                                        <input
                                          type="text"
                                          value={getSelectedCategoryText(insureCategories, String(employee[insureField] || ''))}
                                          onChange={(e) => handleSearchChange(`${employee.Oid}_${insureField}`, e.target.value)}
                                          onFocus={() => {
                                            setSearchTerm(prev => ({...prev, [`${employee.Oid}_${insureField}`]: ''}));
                                            setActiveDropdown(`${employee.Oid}_${insureField}`);
                                          }}
                                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                          placeholder="Даатгал"
                                        />
                                        {activeDropdown === `${employee.Oid}_${insureField}` && (
                                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg overflow-y-auto max-h-32">
                                            {getFilteredCategories(insureCategories, `${employee.Oid}_${insureField}`).map((category, index) => (
                                              <div
                                                key={category.Oid}
                                                onClick={() => {
                                                  setEmployees(prev => prev.map(emp => 
                                                    emp.Oid === employee.Oid 
                                                      ? { ...emp, [insureField]: category.Oid }
                                                      : emp
                                                  ));
                                                  setActiveDropdown(null);
                                                  setSearchTerm(prev => ({...prev, [`${employee.Oid}_${insureField}`]: ''}));
                                                }}
                                                className="px-2 py-1 text-xs cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                                              >
                                                <div className="font-medium">{category.Code}</div>
                                                <div className="text-gray-600 truncate">{category.Name}</div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                        {/* Total row */}
                        <tfoot className="bg-blue-50 border-t-2 border-blue-200">
                          <tr>
                            <td className="px-4 py-3 text-sm font-bold text-gray-900">
                              НИЙТ
                            </td>
                            <td className="px-4 py-3 text-sm font-bold text-gray-900">
                              -
                            </td>
                            {Array.from({ length: 12 }, (_, monthIndex) => {
                              const monthNum = monthIndex + 1;
                              const sField = `S${monthNum}` as keyof SalaryConfigurationData;
                              
                              // Calculate total for this month
                              const totalForMonth = employees.reduce((sum, employee) => {
                                const salary = employee[sField] || 0;
                                return sum + (typeof salary === 'number' ? salary : 0);
                              }, 0);
                              
                              return (
                                <td key={monthNum} className="px-2 py-2">
                                  <div className="text-xs font-bold text-blue-800 bg-blue-100 px-2 py-1 rounded">
                                    {totalForMonth.toLocaleString('mn-MN')}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Single Employee Salary Configuration - Only show when employee is selected */}
              {selectedEmployee && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-800">Сарын цалингийн тохиргоо</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {Array.from({ length: 12 }, (_, i) => {
                    const monthIndex = i + 1;
                    const sField = `S${monthIndex}` as keyof SalaryConfigurationData;
                    const occupationField = `S${monthIndex}_OccupationCategoryId` as keyof SalaryConfigurationData;
                    const insureField = `S${monthIndex}_InsureCategoryId` as keyof SalaryConfigurationData;

                    return (
                      <div key={monthIndex} className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-800 mb-3">{monthIndex}-р сар</h4>
                        
                        {/* Salary Amount */}
                        <div className="mb-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Цалингийн дүн
                          </label>
                          <input
                            type="text"
                            value={formatSalary(salaryData[sField])}
                            onChange={(e) => {
                              const currentValue = e.target.value;
                              const numericValue = parseSalary(currentValue);
                              handleInputChange(sField, numericValue);
                            }}
                            onKeyDown={(e) => {
                              // Allow copy/paste shortcuts
                              const isCtrlCmd = e.ctrlKey || e.metaKey;
                              const isCopy = isCtrlCmd && (e.key === 'c' || e.key === 'C');
                              const isPaste = isCtrlCmd && (e.key === 'v' || e.key === 'V');
                              const isCut = isCtrlCmd && (e.key === 'x' || e.key === 'X');
                              const isSelectAll = isCtrlCmd && (e.key === 'a' || e.key === 'A');
                              const isUndo = isCtrlCmd && (e.key === 'z' || e.key === 'Z');
                              const isRedo = isCtrlCmd && (e.key === 'y' || e.key === 'Y');
                              
                              // Allow navigation and editing keys
                              const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'];
                              const isNumber = /[0-9]/.test(e.key);
                              const isDecimal = e.key === '.';
                              
                              // Allow copy/paste shortcuts and navigation keys
                              if (isCopy || isPaste || isCut || isSelectAll || isUndo || isRedo) {
                                return; // Allow these shortcuts
                              }
                              
                              if (!isNumber && !isDecimal && !allowedKeys.includes(e.key)) {
                                e.preventDefault();
                              }
                              
                              // Only allow one decimal point
                              if (isDecimal && e.currentTarget.value.includes('.')) {
                                e.preventDefault();
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0"
                          />
                        </div>

                        {/* Occupation Category */}
                        <div className="mb-3" ref={occupationDropdownRef}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Мэргэжлийн ангилал
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={searchTerm[occupationField] || getSelectedCategoryText(occupationCategories, String(salaryData[occupationField] || ''))}
                              onChange={(e) => handleSearchChange(occupationField, e.target.value)}
                              onFocus={() => {
                                setSearchTerm(prev => ({...prev, [occupationField]: ''}));
                                setActiveDropdown(occupationField);
                              }}
                              onKeyDown={(e) => handleKeyboardNavigation(occupationField, e, occupationCategories)}
                              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Код эсвэл нэр бичнэ үү..."
                              disabled={categoriesLoading}
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                              <svg
                                className={`w-4 h-4 text-gray-400 transition-transform ${activeDropdown === occupationField ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                            {activeDropdown === occupationField && (
                              <div 
                                className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-xl overflow-y-auto" 
                                style={{ maxHeight: '200px' }}
                                onMouseDown={(e) => e.preventDefault()}
                              >
                                {getFilteredCategories(occupationCategories, occupationField).map((category, index) => (
                                  <div
                                    key={category.Oid}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      console.log('🎯 Clicking occupation category:', category);
                                      handleInputChange(occupationField, category.Oid);
                                      setSearchTerm(prev => ({...prev, [occupationField]: ''}));
                                      setActiveDropdown(null);
                                      setHoveredIndex(prev => ({...prev, [occupationField]: -1}));
                                    }}
                                    onMouseEnter={() => setHoveredIndex(prev => ({...prev, [occupationField]: index}))}
                                    className={`px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors ${
                                      hoveredIndex[occupationField] === index ? 'bg-blue-100 text-blue-900' : 'hover:bg-blue-50 hover:text-blue-900'
                                    }`}
                                  >
                                    <div className="font-medium text-sm">{category.Code}</div>
                                    <div className="text-xs text-gray-600 truncate">{category.Name}</div>
                                  </div>
                                ))}
                                {getFilteredCategories(occupationCategories, occupationField).length === 0 && (
                                  <div className="px-3 py-2 text-gray-500 text-sm">Ангилал олдсонгүй</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Insurance Category */}
                        <div ref={insureDropdownRef}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Даатгалын ангилал
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={searchTerm[insureField] || getSelectedCategoryText(insureCategories, String(salaryData[insureField] || ''))}
                              onChange={(e) => handleSearchChange(insureField, e.target.value)}
                              onFocus={() => {
                                setSearchTerm(prev => ({...prev, [insureField]: ''}));
                                setActiveDropdown(insureField);
                              }}
                              onKeyDown={(e) => handleKeyboardNavigation(insureField, e, insureCategories)}
                              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Код эсвэл нэр бичнэ үү..."
                              disabled={categoriesLoading}
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                              <svg
                                className={`w-4 h-4 text-gray-400 transition-transform ${activeDropdown === insureField ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                            {activeDropdown === insureField && (
                              <div 
                                className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-xl overflow-y-auto" 
                                style={{ maxHeight: '200px' }}
                                onMouseDown={(e) => e.preventDefault()}
                              >
                                {getFilteredCategories(insureCategories, insureField).map((category, index) => (
                                  <div
                                    key={category.Oid}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      console.log('🎯 Clicking insurance category:', category);
                                      handleInputChange(insureField, category.Oid);
                                      setSearchTerm(prev => ({...prev, [insureField]: ''}));
                                      setActiveDropdown(null);
                                      setHoveredIndex(prev => ({...prev, [insureField]: -1}));
                                    }}
                                    onMouseEnter={() => setHoveredIndex(prev => ({...prev, [insureField]: index}))}
                                    className={`px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors ${
                                      hoveredIndex[insureField] === index ? 'bg-blue-100 text-blue-900' : 'hover:bg-blue-50 hover:text-blue-900'
                                    }`}
                                  >
                                    <div className="font-medium text-sm">{category.Code}</div>
                                    <div className="text-xs text-gray-600 truncate">{category.Name}</div>
                                  </div>
                                ))}
                                {getFilteredCategories(insureCategories, insureField).length === 0 && (
                                  <div className="px-3 py-2 text-gray-500 text-sm">Ангилал олдсонгүй</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-between">
          <div className="flex space-x-3">
            {selectedCustomer && employees.length > 0 && (
              <button
                onClick={handleBulkSave}
                disabled={saving || loading}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 flex items-center"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Бүгдийг хадгалж байна...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Бүгдийг хадгалах
                  </>
                )}
              </button>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
            >
              Цуцлах
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading || !selectedEmployee}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Хадгалж байна...
                </>
              ) : (
                'Хадгалах'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalaryConfigurationPopup;
